/**
 * G2eGente - Engenharia de Cargo
 * Lista e gerenciamento de descrições de cargo
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobPositionsAPI, companiesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Plus, 
  Search, 
  MoreVertical,
  Pencil,
  Trash2,
  Briefcase,
  Loader2,
  FilePlus,
  FileText,
  Eye,
  Copy,
  Brain,
  AlertCircle,
  FileDown,
  Users,
  Filter,
  CheckSquare,
  Square,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

export default function JobPositionList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletePosition, setDeletePosition] = useState(null);
  
  // Company info for PDF header
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // PDF Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [includeOccupants, setIncludeOccupants] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [breakByPerson, setBreakByPerson] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  
  // New: Selection and filter state
  const [selectedPositions, setSelectedPositions] = useState(new Set());
  const [filterWithOccupants, setFilterWithOccupants] = useState(false);
  const [occupantsMap, setOccupantsMap] = useState({});
  const [loadingOccupants, setLoadingOccupants] = useState(false);

  useEffect(() => {
    fetchPositions();
    fetchCompanyInfo();
    
    // Show message if redirected from job without cargo
    if (location.state?.showMessage) {
      toast.info(location.state.showMessage, { duration: 5000 });
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchPositions = async () => {
    try {
      const res = await jobPositionsAPI.getAll();
      setPositions(res.data);
    } catch (error) {
      toast.error('Erro ao carregar cargos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const res = await companiesAPI.getMe();
      setCompanyInfo(res.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  // Fetch occupants for all positions when modal opens
  const fetchAllOccupants = useCallback(async () => {
    setLoadingOccupants(true);
    const newMap = {};
    
    for (const position of positions) {
      try {
        const res = await jobPositionsAPI.getOccupants(position.id);
        newMap[position.id] = res.data.ocupantes || [];
      } catch (err) {
        newMap[position.id] = [];
      }
    }
    
    setOccupantsMap(newMap);
    setLoadingOccupants(false);
  }, [positions]);

  // When export modal opens, fetch occupants
  useEffect(() => {
    if (showExportModal && positions.length > 0 && Object.keys(occupantsMap).length === 0) {
      fetchAllOccupants();
    }
  }, [showExportModal, positions, occupantsMap, fetchAllOccupants]);

  const handleDelete = async () => {
    if (!deletePosition) return;
    try {
      await jobPositionsAPI.delete(deletePosition.id);
      toast.success('Cargo excluído com sucesso');
      setPositions(positions.filter(p => p.id !== deletePosition.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cargo');
    } finally {
      setDeletePosition(null);
    }
  };

  const handleDuplicate = async (position) => {
    try {
      const newData = {
        ...position,
        titulo: `${position.titulo} (Cópia)`,
      };
      delete newData.id;
      delete newData.company_id;
      delete newData.created_at;
      delete newData.updated_at;
      delete newData.vagas_count;
      
      const res = await jobPositionsAPI.create(newData);
      setPositions([res.data, ...positions]);
      toast.success('Cargo duplicado com sucesso');
    } catch (error) {
      toast.error('Erro ao duplicar cargo');
    }
  };

  const handleCreateJob = (position) => {
    // Navigate to job creation with the position pre-selected
    navigate('/company/jobs/new', { state: { fromPosition: position } });
  };

  // Exportar descrições de cargos selecionados para PDF
  const handleExportPDF = async () => {
    // Get positions to export based on selection
    const positionsToExport = getFilteredPositionsForExport();
    
    if (positionsToExport.length === 0) {
      toast.warning('Nenhum cargo selecionado para exportar');
      return;
    }
    
    setExportLoading(true);
    
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let y = 20;
      
      // ========================================
      // CABEÇALHO COM LOGO E NOME DA EMPRESA
      // ========================================
      const headerHeight = 35;
      
      // Try to load company logo
      let logoLoaded = false;
      if (companyInfo?.logo_url) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = companyInfo.logo_url;
          });
          
          // Add logo (max height 25mm, proportional width)
          const maxHeight = 25;
          const ratio = img.width / img.height;
          const logoHeight = Math.min(maxHeight, 25);
          const logoWidth = logoHeight * ratio;
          
          pdf.addImage(img, 'PNG', margin, y, logoWidth, logoHeight);
          logoLoaded = true;
          
          // Company name next to logo
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 41, 59);
          pdf.text(companyInfo.nome || 'Empresa', margin + logoWidth + 5, y + 10);
          
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100, 116, 139);
          if (companyInfo.cnpj) {
            pdf.text(`CNPJ: ${companyInfo.cnpj}`, margin + logoWidth + 5, y + 16);
          }
          
          y += headerHeight;
        } catch (logoError) {
          console.log('Could not load logo, using text header');
        }
      }
      
      // If no logo, use text-only header
      if (!logoLoaded && companyInfo?.nome) {
        pdf.setFillColor(30, 64, 175);
        pdf.rect(margin, y, contentWidth, 15, 'F');
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(companyInfo.nome, pageWidth / 2, y + 7, { align: 'center' });
        
        if (companyInfo.cnpj) {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`CNPJ: ${companyInfo.cnpj}`, pageWidth / 2, y + 12, { align: 'center' });
        }
        
        y += 20;
      }
      
      // Função auxiliar para verificar e adicionar nova página (com cabeçalho repetido)
      const checkPageBreak = (requiredSpace) => {
        if (y + requiredSpace > pageHeight - 20) {
          pdf.addPage();
          y = 20;
          return true;
        }
        return false;
      };
      
      // Função para adicionar seção com título
      const addSection = (title, content, isList = false) => {
        if (!content || (Array.isArray(content) && content.length === 0)) return;
        
        checkPageBreak(20);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175); // blue-800
        pdf.text(title, margin, y);
        y += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(9);
        
        if (isList && Array.isArray(content)) {
          content.forEach((item) => {
            checkPageBreak(6);
            const itemText = typeof item === 'object' ? 
              `${item.categoria || ''}: ${(item.itens || []).join('; ')}` : 
              String(item);
            const lines = pdf.splitTextToSize(`• ${itemText}`, contentWidth - 5);
            lines.forEach(line => {
              pdf.text(line, margin + 3, y);
              y += 4.5;
            });
          });
        } else {
          const text = Array.isArray(content) ? content.join(', ') : String(content);
          const lines = pdf.splitTextToSize(text, contentWidth);
          lines.forEach(line => {
            checkPageBreak(5);
            pdf.text(line, margin, y);
            y += 4.5;
          });
        }
        y += 3;
      };
      
      // Título do documento
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('DESCRIÇÕES DE CARGOS', pageWidth / 2, y, { align: 'center' });
      y += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, pageWidth / 2, y, { align: 'center' });
      y += 5;
      pdf.text(`Total: ${positionsToExport.length} cargo(s)`, pageWidth / 2, y, { align: 'center' });
      y += 15;
      
      // Iterar sobre cada cargo selecionado
      for (let index = 0; index < positionsToExport.length; index++) {
        const position = positionsToExport[index];
        
        // Verificar espaço para novo cargo (mínimo 80mm)
        checkPageBreak(80);
        
        // ========================================
        // CABEÇALHO: Nomenclatura | CBO | Código | Jornada
        // ========================================
        pdf.setFillColor(30, 64, 175); // blue-800
        pdf.rect(margin, y, contentWidth, 12, 'F');
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        
        // Linha 1: Título do cargo
        pdf.text(`${index + 1}. ${position.titulo || 'Sem título'}`, margin + 3, y + 5);
        
        // Linha 2: CBO | Código | Jornada
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        const headerInfo = [];
        if (position.codigo_cbo) headerInfo.push(`CBO: ${position.codigo_cbo}`);
        if (position.codigo_cargo) headerInfo.push(`Código: ${position.codigo_cargo}`);
        if (position.jornada_diaria || position.jornada_semanal) {
          headerInfo.push(`Jornada: ${position.jornada_diaria || '08:00'}/${position.jornada_semanal || '44h'}`);
        }
        pdf.text(headerInfo.join(' | '), margin + 3, y + 10);
        y += 16;
        
        pdf.setTextColor(50, 50, 50);
        
        // ========================================
        // MISSÃO DO CARGO
        // ========================================
        if (position.descricao_sumaria) {
          addSection('MISSÃO DO CARGO', position.descricao_sumaria);
        }
        
        // ========================================
        // DESCRIÇÃO COMPLETA (Principais Responsabilidades)
        // ========================================
        if (position.responsabilidades && position.responsabilidades.length > 0) {
          checkPageBreak(20);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text('DESCRIÇÃO COMPLETA DO CARGO (Principais Responsabilidades)', margin, y);
          y += 6;
          
          pdf.setTextColor(50, 50, 50);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          
          position.responsabilidades.forEach((resp) => {
            if (typeof resp === 'object' && resp.categoria) {
              // Categoria com itens
              checkPageBreak(10);
              pdf.setFont('helvetica', 'bold');
              pdf.text(`${resp.categoria}:`, margin + 3, y);
              y += 5;
              pdf.setFont('helvetica', 'normal');
              
              (resp.itens || []).forEach((item) => {
                checkPageBreak(5);
                const lines = pdf.splitTextToSize(`• ${item}`, contentWidth - 10);
                lines.forEach(line => {
                  pdf.text(line, margin + 8, y);
                  y += 4.5;
                });
              });
              y += 2;
            } else {
              // Item simples
              checkPageBreak(5);
              const lines = pdf.splitTextToSize(`• ${resp}`, contentWidth - 5);
              lines.forEach(line => {
                pdf.text(line, margin + 3, y);
                y += 4.5;
              });
            }
          });
          y += 3;
        }
        
        // ========================================
        // PRÉ-REQUISITOS
        // ========================================
        const hasPreRequisitos = position.escolaridade || position.escolaridade_desejavel || 
                                  position.experiencia || position.informatica || 
                                  position.informatica_avancado || 
                                  (position.conhecimentos_especificos && position.conhecimentos_especificos.length > 0) ||
                                  (position.treinamentos_corporativos && position.treinamentos_corporativos.length > 0) ||
                                  (position.requisitos_legais && (Array.isArray(position.requisitos_legais) ? position.requisitos_legais.length > 0 : position.requisitos_legais));
        
        if (hasPreRequisitos) {
          checkPageBreak(20);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text('PRÉ-REQUISITOS', margin, y);
          y += 6;
          
          pdf.setTextColor(50, 50, 50);
          pdf.setFontSize(9);
          
          const addRequisito = (label, value) => {
            if (!value) return;
            checkPageBreak(6);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${label}: `, margin + 3, y);
            const labelWidth = pdf.getTextWidth(`${label}: `);
            pdf.setFont('helvetica', 'normal');
            const valueLines = pdf.splitTextToSize(value, contentWidth - labelWidth - 8);
            pdf.text(valueLines[0], margin + 3 + labelWidth, y);
            y += 4.5;
            valueLines.slice(1).forEach(line => {
              pdf.text(line, margin + 3, y);
              y += 4.5;
            });
          };
          
          addRequisito('Escolaridade Mínima', position.escolaridade);
          addRequisito('Escolaridade Desejável', position.escolaridade_desejavel);
          addRequisito('Experiência', position.experiencia);
          addRequisito('Informática Básica', position.informatica);
          addRequisito('Informática Avançada', position.informatica_avancado);
          
          if (position.conhecimentos_especificos && position.conhecimentos_especificos.length > 0) {
            addRequisito('Conhecimentos Específicos', position.conhecimentos_especificos.filter(k => k).join(', '));
          }
          
          if (position.treinamentos_corporativos && position.treinamentos_corporativos.length > 0) {
            const treinamentos = Array.isArray(position.treinamentos_corporativos) 
              ? position.treinamentos_corporativos.filter(t => t).join(', ')
              : position.treinamentos_corporativos;
            addRequisito('Treinamentos Corporativos', treinamentos);
          }
          
          if (position.requisitos_legais) {
            const requisitosLegais = Array.isArray(position.requisitos_legais) 
              ? position.requisitos_legais.filter(r => r).join(', ')
              : position.requisitos_legais;
            addRequisito('Requisitos Legais', requisitosLegais);
          }
          
          y += 3;
        }
        
        // ========================================
        // HABILIDADES E COMPETÊNCIAS
        // ========================================
        if (position.habilidades_competencias && position.habilidades_competencias.length > 0) {
          const habilidades = position.habilidades_competencias.filter(h => h);
          if (habilidades.length > 0) {
            addSection('HABILIDADES E COMPETÊNCIAS', habilidades.join(', '));
          }
        }
        
        // ========================================
        // LINHA DE CARREIRA
        // ========================================
        if (position.linha_carreira) {
          addSection('LINHA DE CARREIRA', position.linha_carreira);
        }
        
        // ========================================
        // OCUPANTES DO CARGO (opcional)
        // ========================================
        if (includeOccupants && occupantsMap[position.id] && occupantsMap[position.id].length > 0) {
          checkPageBreak(30);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text(`OCUPANTES DO CARGO (${occupantsMap[position.id].length})`, margin, y);
          y += 6;
          
          pdf.setTextColor(50, 50, 50);
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          
          occupantsMap[position.id].forEach((emp, empIdx) => {
            checkPageBreak(includeSignature ? 12 : 6);
            
            const empInfo = `${empIdx + 1}. ${emp.nome}${emp.matricula ? ` (Mat: ${emp.matricula})` : ''}${emp.departamento ? ` - ${emp.departamento}` : ''}`;
            pdf.text(empInfo, margin + 3, y);
            
            if (includeSignature) {
              // Linha de assinatura
              y += 6;
              pdf.setDrawColor(150, 150, 150);
              pdf.line(pageWidth - margin - 60, y, pageWidth - margin, y);
              pdf.setFontSize(7);
              pdf.setTextColor(150, 150, 150);
              pdf.text('Assinatura', pageWidth - margin - 30, y + 3, { align: 'center' });
              pdf.setTextColor(50, 50, 50);
              pdf.setFontSize(9);
              y += 6;
            } else {
              y += 5;
            }
          });
          y += 3;
        }
        
        // Quebra de página entre cargos (cada cargo em uma página separada)
        if (index < positionsToExport.length - 1) {
          pdf.addPage();
          y = 20;
        }
      }
      
      // Rodapé em todas as páginas
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 30, pageHeight - 10);
        pdf.text(companyInfo?.nome || 'G2eGente - Engenharia de Cargos', margin, pageHeight - 10);
      }
      
      pdf.save(`descricoes_cargos_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado com sucesso!');
      setShowExportModal(false);
      // Reset selection after export
      setSelectedPositions(new Set());
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportLoading(false);
    }
  };

  // Exportar PDF com quebra por pessoa (um ocupante por página)
  const handleExportPDFByPerson = async () => {
    const positionsToExport = getFilteredPositionsForExport();
    
    if (positionsToExport.length === 0) {
      toast.warning('Nenhum cargo selecionado para exportar');
      return;
    }
    
    setExportLoading(true);
    
    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let isFirstPage = true;
      
      // Buscar histórico de ocupantes para cada cargo
      const occupantsHistoryMap = {};
      for (const position of positionsToExport) {
        try {
          const res = await jobPositionsAPI.getOccupantsHistory(position.id);
          occupantsHistoryMap[position.id] = res.data.occupants || [];
        } catch (err) {
          console.error(`Erro ao buscar ocupantes do cargo ${position.titulo}:`, err);
          occupantsHistoryMap[position.id] = [];
        }
      }
      
      // Função auxiliar para adicionar cabeçalho da empresa
      const addCompanyHeader = () => {
        let y = 15;
        
        if (companyInfo?.nome) {
          pdf.setFillColor(30, 64, 175);
          pdf.rect(margin, y, contentWidth, 12, 'F');
          
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          pdf.text(companyInfo.nome, pageWidth / 2, y + 6, { align: 'center' });
          
          if (companyInfo.cnpj) {
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`CNPJ: ${companyInfo.cnpj}`, pageWidth / 2, y + 10, { align: 'center' });
          }
          
          return y + 18;
        }
        return y;
      };
      
      // Função auxiliar para verificar e adicionar nova página
      const checkPageBreak = (y, requiredSpace) => {
        if (y + requiredSpace > pageHeight - 25) {
          pdf.addPage();
          return addCompanyHeader();
        }
        return y;
      };
      
      // Função para adicionar seção com título
      const addSection = (title, content, isList, startY) => {
        let y = startY;
        if (!content || (Array.isArray(content) && content.length === 0)) return y;
        
        y = checkPageBreak(y, 15);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text(title, margin, y);
        y += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(50, 50, 50);
        pdf.setFontSize(8);
        
        if (isList && Array.isArray(content)) {
          content.forEach((item) => {
            y = checkPageBreak(y, 5);
            const itemText = typeof item === 'object' ? 
              `${item.categoria || ''}: ${(item.itens || []).join('; ')}` : 
              String(item);
            const lines = pdf.splitTextToSize(`• ${itemText}`, contentWidth - 5);
            lines.forEach(line => {
              pdf.text(line, margin + 3, y);
              y += 4;
            });
          });
        } else {
          const text = Array.isArray(content) ? content.join(', ') : String(content);
          const lines = pdf.splitTextToSize(text, contentWidth);
          lines.forEach(line => {
            y = checkPageBreak(y, 4);
            pdf.text(line, margin, y);
            y += 4;
          });
        }
        return y + 2;
      };
      
      // Iterar sobre cada cargo
      for (const position of positionsToExport) {
        const occupants = occupantsHistoryMap[position.id] || [];
        
        // Se não tem ocupantes, pular o cargo
        if (occupants.length === 0) continue;
        
        // Iterar sobre cada ocupante
        for (const occupant of occupants) {
          // Nova página para cada ocupante
          if (!isFirstPage) {
            pdf.addPage();
          }
          isFirstPage = false;
          
          let y = addCompanyHeader();
          
          // ========================================
          // CABEÇALHO: Ocupante e Período
          // ========================================
          pdf.setFillColor(15, 118, 110); // teal-700
          pdf.rect(margin, y, contentWidth, 18, 'F');
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          pdf.text(`OCUPANTE: ${occupant.nome || 'N/A'}`, margin + 3, y + 6);
          
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          // Período: se data_fim é "Atual" ou não existe, mostrar apenas data de início
          const periodoText = occupant.data_fim && occupant.data_fim_formatada !== 'Atual'
            ? `Período: ${occupant.data_inicio_formatada || '-'} até ${occupant.data_fim_formatada}`
            : `Período: ${occupant.data_inicio_formatada || '-'}`;
          pdf.text(periodoText, margin + 3, y + 11);
          
          // Matrícula e Departamento
          const infoExtra = [];
          if (occupant.matricula) infoExtra.push(`Mat: ${occupant.matricula}`);
          if (occupant.departamento) infoExtra.push(occupant.departamento);
          if (occupant.setor) infoExtra.push(occupant.setor);
          if (infoExtra.length > 0) {
            pdf.text(infoExtra.join(' | '), margin + 3, y + 16);
          }
          
          y += 22;
          
          // ========================================
          // CABEÇALHO: Cargo
          // ========================================
          pdf.setFillColor(30, 64, 175); // blue-800
          pdf.rect(margin, y, contentWidth, 10, 'F');
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          pdf.text(`CARGO: ${position.titulo || 'Sem título'}`, margin + 3, y + 6);
          
          // CBO e Código
          const cargoInfo = [];
          if (position.codigo_cbo) cargoInfo.push(`CBO: ${position.codigo_cbo}`);
          if (position.codigo_cargo) cargoInfo.push(`Código: ${position.codigo_cargo}`);
          if (cargoInfo.length > 0) {
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            pdf.text(cargoInfo.join(' | '), pageWidth - margin - 3, y + 6, { align: 'right' });
          }
          
          y += 14;
          pdf.setTextColor(50, 50, 50);
          
          // ========================================
          // MISSÃO DO CARGO
          // ========================================
          if (position.descricao_sumaria) {
            y = addSection('MISSÃO DO CARGO', position.descricao_sumaria, false, y);
          }
          
          // ========================================
          // DESCRIÇÃO COMPLETA
          // ========================================
          if (position.responsabilidades && position.responsabilidades.length > 0) {
            y = checkPageBreak(y, 15);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(30, 64, 175);
            pdf.text('DESCRIÇÃO COMPLETA DO CARGO', margin, y);
            y += 5;
            
            pdf.setTextColor(50, 50, 50);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            
            for (const resp of position.responsabilidades) {
              if (typeof resp === 'object' && resp.categoria) {
                y = checkPageBreak(y, 8);
                pdf.setFont('helvetica', 'bold');
                pdf.text(`${resp.categoria}:`, margin + 3, y);
                y += 4;
                pdf.setFont('helvetica', 'normal');
                
                for (const item of (resp.itens || [])) {
                  y = checkPageBreak(y, 4);
                  const lines = pdf.splitTextToSize(`• ${item}`, contentWidth - 10);
                  lines.forEach(line => {
                    pdf.text(line, margin + 6, y);
                    y += 3.5;
                  });
                }
              } else {
                y = checkPageBreak(y, 4);
                const lines = pdf.splitTextToSize(`• ${resp}`, contentWidth - 5);
                lines.forEach(line => {
                  pdf.text(line, margin + 3, y);
                  y += 3.5;
                });
              }
            }
            y += 2;
          }
          
          // ========================================
          // PRÉ-REQUISITOS (resumido)
          // ========================================
          const requisitos = [];
          if (position.escolaridade) requisitos.push(`Escolaridade: ${position.escolaridade}`);
          if (position.experiencia) requisitos.push(`Experiência: ${position.experiencia}`);
          if (position.informatica) requisitos.push(`Informática: ${position.informatica}`);
          
          if (requisitos.length > 0) {
            y = addSection('PRÉ-REQUISITOS', requisitos.join(' | '), false, y);
          }
          
          // ========================================
          // HABILIDADES E COMPETÊNCIAS
          // ========================================
          if (position.habilidades_competencias && position.habilidades_competencias.length > 0) {
            const habilidades = position.habilidades_competencias.filter(h => h);
            if (habilidades.length > 0) {
              y = addSection('HABILIDADES E COMPETÊNCIAS', habilidades.join(', '), false, y);
            }
          }
          
          // ========================================
          // CAMPO DE ASSINATURA (opcional)
          // ========================================
          if (includeSignature) {
            y = checkPageBreak(y, 30);
            y += 15;
            
            pdf.setDrawColor(100, 100, 100);
            pdf.line(margin + 20, y, pageWidth - margin - 20, y);
            
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text('Assinatura do Ocupante', pageWidth / 2, y + 5, { align: 'center' });
            pdf.text(`Data: ____/____/________`, pageWidth / 2, y + 10, { align: 'center' });
          }
        }
      }
      
      // Verificar se alguma página foi gerada
      if (isFirstPage) {
        toast.warning('Nenhum cargo com ocupantes encontrado para exportar');
        setExportLoading(false);
        return;
      }
      
      // Rodapé em todas as páginas (apenas número da página)
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 25, pageHeight - 8);
      }
      
      pdf.save(`descricoes_cargos_por_pessoa_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exportado com sucesso!');
      setShowExportModal(false);
      setSelectedPositions(new Set());
    } catch (error) {
      console.error('Erro ao exportar PDF por pessoa:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportLoading(false);
    }
  };

  // Get positions filtered for export modal
  const getFilteredPositionsForExport = () => {
    let filtered = positions;
    
    // Apply occupants filter
    if (filterWithOccupants) {
      filtered = filtered.filter(p => (occupantsMap[p.id] || []).length > 0);
    }
    
    // If specific positions are selected, use only those
    if (selectedPositions.size > 0) {
      filtered = filtered.filter(p => selectedPositions.has(p.id));
    }
    
    return filtered;
  };

  // Toggle single position selection
  const togglePositionSelection = (positionId) => {
    const newSet = new Set(selectedPositions);
    if (newSet.has(positionId)) {
      newSet.delete(positionId);
    } else {
      newSet.add(positionId);
    }
    setSelectedPositions(newSet);
  };

  // Select all visible positions
  const selectAllPositions = () => {
    let positionsToSelect = positions;
    if (filterWithOccupants) {
      positionsToSelect = positions.filter(p => (occupantsMap[p.id] || []).length > 0);
    }
    setSelectedPositions(new Set(positionsToSelect.map(p => p.id)));
  };

  // Deselect all
  const deselectAllPositions = () => {
    setSelectedPositions(new Set());
  };

  // Check if all visible positions are selected
  const areAllSelected = () => {
    let positionsToCheck = positions;
    if (filterWithOccupants) {
      positionsToCheck = positions.filter(p => (occupantsMap[p.id] || []).length > 0);
    }
    return positionsToCheck.length > 0 && positionsToCheck.every(p => selectedPositions.has(p.id));
  };

  const filteredPositions = positions.filter(pos => {
    if (!search) return true;
    return pos.titulo.toLowerCase().includes(search.toLowerCase()) ||
           pos.descricao_sumaria?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="job-position-list">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Engenharia de Cargo</h1>
            <p className="text-slate-600">Crie e gerencie descrições de cargo para suas vagas</p>
          </div>
          <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowExportModal(true)}
            disabled={positions.length === 0}
            data-testid="export-positions-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
          <Button 
            onClick={() => navigate('/company/job-positions/new')}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="new-position-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cargo
          </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por título ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Positions List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : filteredPositions.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <FilePlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {positions.length === 0 ? 'Nenhum cargo cadastrado' : 'Nenhum resultado encontrado'}
              </h3>
              <p className="text-slate-600 mb-6">
                {positions.length === 0 
                  ? 'Crie descrições de cargo padronizadas para usar em suas vagas'
                  : 'Tente uma busca diferente'}
              </p>
              {positions.length === 0 && (
                <Button 
                  onClick={() => navigate('/company/job-positions/new')}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Cargo
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredPositions.map((position) => (
              <Card 
                key={position.id} 
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/company/job-positions/${position.id}`)}
                data-testid={`position-item-${position.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">{position.titulo}</h3>
                          {position.codigo_cargo && (
                            <span className="text-sm text-slate-500">Código: {position.codigo_cargo}</span>
                          )}
                        </div>
                      </div>
                      
                      {position.descricao_sumaria && (
                        <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                          {position.descricao_sumaria}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2">
                        {position.vagas_count > 0 && (
                          <Badge className="bg-green-100 text-green-700">
                            <Briefcase className="w-3 h-3 mr-1" />
                            {position.vagas_count} vaga(s)
                          </Badge>
                        )}
                        {position.escolaridade && (
                          <Badge variant="outline">{position.escolaridade}</Badge>
                        )}
                        {position.experiencia && (
                          <Badge variant="outline">{position.experiencia}</Badge>
                        )}
                        {position.perfil_cargo?.disc_desejado && (
                          <Badge className="bg-purple-100 text-purple-700">
                            DISC: {position.perfil_cargo.disc_desejado}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/company/job-positions/${position.id}/edit`, { state: { focusSection: 'perfil' } })}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Brain className="w-4 h-4 mr-1" />
                        Perfil Ideal
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCreateJob(position)}
                        className="border-green-300 text-green-700 hover:bg-green-50"
                      >
                        <Briefcase className="w-4 h-4 mr-1" />
                        Criar Vaga
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/company/job-positions/${position.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/company/job-positions/${position.id}/edit`)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(position)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeletePosition(position)}
                            disabled={position.vagas_count > 0}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletePosition} onOpenChange={() => setDeletePosition(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Cargo</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o cargo &quot;{deletePosition?.titulo}&quot;? 
                Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Export PDF Configuration Modal */}
        <Dialog open={showExportModal} onOpenChange={(open) => {
          setShowExportModal(open);
          if (!open) {
            // Reset selection when closing
            setSelectedPositions(new Set());
            setFilterWithOccupants(false);
            setBreakByPerson(false);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileDown className="w-5 h-5 text-cyan-600" />
                Exportar Descrições de Cargo
              </DialogTitle>
              <DialogDescription>
                Selecione os cargos e configure as opções de exportação
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Filter and Selection Controls */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-900">Seleção de Cargos</h4>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={selectAllPositions}
                      disabled={loadingOccupants}
                    >
                      <CheckSquare className="w-4 h-4 mr-1" />
                      Todos
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={deselectAllPositions}
                      disabled={selectedPositions.size === 0}
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Limpar
                    </Button>
                  </div>
                </div>

                {/* Filter by occupants */}
                <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="filterWithOccupants"
                    checked={filterWithOccupants}
                    onCheckedChange={(checked) => {
                      setFilterWithOccupants(checked);
                      // Clear selection when changing filter
                      setSelectedPositions(new Set());
                    }}
                    disabled={loadingOccupants}
                  />
                  <div className="grid gap-1 leading-none">
                    <Label htmlFor="filterWithOccupants" className="flex items-center gap-2 cursor-pointer">
                      <Filter className="w-4 h-4 text-slate-500" />
                      Exibir apenas cargos com ocupantes ativos
                    </Label>
                    <p className="text-xs text-slate-500">
                      Filtra para mostrar somente cargos que possuem funcionários vinculados
                    </p>
                  </div>
                </div>

                {/* Positions list with checkboxes */}
                <div className="border rounded-lg max-h-[250px] overflow-y-auto">
                  {loadingOccupants ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-600 mr-2" />
                      <span className="text-slate-500">Carregando informações...</span>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {positions
                        .filter(p => !filterWithOccupants || (occupantsMap[p.id] || []).length > 0)
                        .map(position => {
                          const occupantsCount = (occupantsMap[position.id] || []).length;
                          const isSelected = selectedPositions.has(position.id);
                          
                          return (
                            <div 
                              key={position.id}
                              className={`flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer ${isSelected ? 'bg-cyan-50' : ''}`}
                              onClick={() => togglePositionSelection(position.id)}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => togglePositionSelection(position.id)}
                                />
                                <div>
                                  <p className="font-medium text-slate-900">{position.titulo}</p>
                                  <p className="text-xs text-slate-500">
                                    {position.codigo_cbo ? `CBO: ${position.codigo_cbo}` : 'Sem CBO'}
                                    {position.codigo_cargo && ` | Código: ${position.codigo_cargo}`}
                                  </p>
                                </div>
                              </div>
                              <Badge variant={occupantsCount > 0 ? 'default' : 'outline'} className={occupantsCount > 0 ? 'bg-green-100 text-green-700' : ''}>
                                <Users className="w-3 h-3 mr-1" />
                                {occupantsCount} ocupante{occupantsCount !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          );
                        })}
                      
                      {positions.filter(p => !filterWithOccupants || (occupantsMap[p.id] || []).length > 0).length === 0 && (
                        <div className="p-8 text-center text-slate-500">
                          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>Nenhum cargo com ocupantes ativos encontrado</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* What will be included */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">O relatório incluirá:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {companyInfo?.logo_url && (
                    <li className="flex items-center gap-2 text-cyan-700">
                      <Building2 className="w-4 h-4" />
                      Cabeçalho com logo e nome da empresa
                    </li>
                  )}
                  <li>• Nomenclatura do cargo | CBO | Código | Jornada</li>
                  <li>• Missão do cargo</li>
                  <li>• Descrição completa (Principais Responsabilidades)</li>
                  <li>• Pré-requisitos</li>
                  <li>• Habilidades e competências</li>
                  <li>• Linha de carreira</li>
                </ul>
              </div>

              {/* Additional options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="includeOccupants"
                    checked={includeOccupants}
                    onCheckedChange={setIncludeOccupants}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label htmlFor="includeOccupants" className="flex items-center gap-2 cursor-pointer">
                      <Users className="w-4 h-4 text-slate-500" />
                      Incluir ocupantes do cargo
                    </Label>
                    <p className="text-xs text-slate-500">
                      Lista os funcionários que ocupam cada cargo
                    </p>
                  </div>
                </div>

                {includeOccupants && (
                  <div className="flex items-center space-x-3 ml-7">
                    <Checkbox
                      id="includeSignature"
                      checked={includeSignature}
                      onCheckedChange={setIncludeSignature}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="includeSignature" className="cursor-pointer">
                        Adicionar campo de assinatura
                      </Label>
                      <p className="text-xs text-slate-500">
                        Linha para assinatura na frente de cada nome
                      </p>
                    </div>
                  </div>
                )}

                {includeOccupants && (
                  <div className="flex items-center space-x-3 ml-7 p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <Checkbox
                      id="breakByPerson"
                      checked={breakByPerson}
                      onCheckedChange={setBreakByPerson}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="breakByPerson" className="cursor-pointer font-medium text-teal-800">
                        Quebra por pessoa (uma página por ocupante)
                      </Label>
                      <p className="text-xs text-teal-600">
                        Cada ocupante em página separada com período de ocupação do cargo 
                        (data de admissão ou movimentação até a data atual ou saída)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className={`p-3 rounded-lg ${getFilteredPositionsForExport().length === 0 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm ${getFilteredPositionsForExport().length === 0 ? 'text-red-700' : 'text-amber-700'}`}>
                  <strong>Total a exportar:</strong> {getFilteredPositionsForExport().length} cargo(s)
                  {selectedPositions.size > 0 && ` (${selectedPositions.size} selecionado${selectedPositions.size !== 1 ? 's' : ''})`}
                  {selectedPositions.size === 0 && ' (todos os cargos visíveis)'}
                  {breakByPerson && ' - Uma página por ocupante'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={breakByPerson ? handleExportPDFByPerson : handleExportPDF}
                disabled={exportLoading || getFilteredPositionsForExport().length === 0}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {exportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Exportar PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
